export const invoiceAgentsStyles = {
  // Container styles
  container: {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    padding: "0 16px",
    overflowX: "hidden", // Only prevent horizontal overflow
    boxSizing: "border-box",
  },

  // Header section styles
  headerSection: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
  },

  backIcon: {
    cursor: "pointer",
    marginRight: "10px",
  },

  headerTitle: {
    margin: 0,
    fontSize: "1.5rem",
    fontWeight: "bold",
  },

  // Menu button section styles
  menuButtonSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    width: "100%",
    flexWrap: "wrap",
    gap: "10px",
  },

  // Overview menu buttons styles
  overviewMenuButtons: {
    container: (isEditMode) => ({
      width: isEditMode ? "200px" : "460px", // Reduced from 535px to match blue tile width
      height: "22px",
      flex: "0 0 auto", // Don't grow or shrink
    }),
    button: (isEditMode) => ({
      fontSize: "1rem",
      height: "50px",
      width: isEditMode ? "100%" : "90%",
    }),
  },

  // Next button styles (for create mode)
  nextButton: {
    borderRadius: "25px",
    width: "120px",
    backgroundColor: "#3B5CFF",
    zIndex: 10,
  },

  // Update button styles (for edit mode)
  updateButton: {
    borderRadius: "25px",
    width: "120px",
    background: "linear-gradient(135deg, #2AD899 0%, #1E8A6B 100%)",
    boxShadow: "0 4px 15px rgba(42, 216, 153, 0.3)",
    zIndex: 10,
    "&:hover": {
      background: "linear-gradient(135deg, #1E8A6B 0%, #2AD899 100%)",
      boxShadow: "0 6px 20px rgba(42, 216, 153, 0.4)",
      transform: "translateY(-2px)",
    },
    transition: "all 0.3s ease",
  },

  // Blue tile styles
  blueTile: {
    container: (receivedData) => ({
      width: "100%",
      maxWidth: "460px", // Maintain original width
      marginTop: "0px",
      height: "auto",
      minHeight: "165px",
      backgroundColor: receivedData?.name === "Policy" && "#2AD899",
    }),
    image: {
      width: "80px",
    },
  },

  // Main content layout - Proper alignment
  mainContent: {
    display: "flex",
    flexDirection: "row",
    gap: "24px", // Increased from 8px to give proper spacing
    marginTop: "0px",
    width: "100%",
    maxWidth: "100%",
    alignItems: "flex-start",
    justifyContent: "flex-start", // Keep them close but not collapsing
    overflowX: "hidden", // Only prevent horizontal overflow
    flexWrap: "wrap",
  },

  // Left content area - Blue tile with proper spacing
  leftContent: {
    flex: "0 0 auto",
    width: "460px", // Maintain original width
    maxWidth: "460px",
    marginTop: "16px", // Small spacing below overview menu
    minWidth: "460px", // Ensure minimum width
  },

  // Right content area - Prompts aligned with overview menu
  rightContent: {
    flex: "1", // Take remaining space
    width: "auto", // Allow flexible width 
    marginLeft: "50px", // Removed auto margin to keep it close to blue tile
    marginTop: "0px", // Align with overview menu
    marginRight: "30px", // Add right margin for breathing room
    backgroundColor: "transparent", // Ensure transparent background
    background: "transparent", // Ensure no background
    maxWidth: "calc(100% - 50px - 30px)",
    overflowX: "hidden", // Only prevent horizontal overflow
    minWidth: "300px", // Ensure minimum width for prompts
  },

  // Bottom content area - Below blue tile and prompts
  bottomContent: {
    width: "100%",
    maxWidth: "100%",
    marginTop: "16px", // Reduced from 24px
    paddingTop: "16px", // Reduced from 24px
    borderTop: "1px solid #e0e0e0",
    overflowX: "hidden", // Only prevent horizontal overflow
    marginRight: 0,
    paddingRight: 0,
  },

  // Review section styles - now responsive
  reviewSection: {
    width: "100%",
    height: "auto",
    minHeight: "auto", // Changed from 300px to auto
    backgroundColor: "transparent", // Ensure transparent background
    background: "transparent", // Ensure no background
    position: "relative", // Use relative positioning instead of absolute
    marginTop: "0px", // Ensure no top margin
    marginLeft: "0px", // Ensure no left margin
    overflowX: "hidden", // Only prevent horizontal overflow
    maxWidth: "100%", // Ensure it doesn't exceed container width
  },

  // Fields section styles - now responsive
  fieldsSection: {
    height: "auto",
    backgroundColor: "transparent", // Ensure transparent background
    background: "transparent", // Ensure no background
  },

  // Form section styles
  formSection: {
    marginTop: "20px",
    width: "100%",
  },

  // Footer styles
  footer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: "20px",
    padding: "20px 0",
    flexWrap: "wrap",
    gap: "10px",
  },

  footerBackButton: {
    borderRadius: "25px",
    padding: "10px 20px",
    textTransform: "none",
    fontWeight: "bold",
  },

  footerSaveButton: {
    borderRadius: "25px",
    padding: "10px 20px",
    textTransform: "none",
    fontWeight: "bold",
    backgroundColor: "#3B5CFF",
    color: "white",
    "&:hover": {
      backgroundColor: "#2A4BFF",
    },
  },

  // Snackbar styles
  snackbar: {
    anchorOrigin: { vertical: "top", horizontal: "right" },
  },

  // Typography styles
  buttonText: {
    fontSize: "1rem",
    fontWeight: "bold",
  },
};

export const getButtonStyles = (isEditMode) => ({
  container: invoiceAgentsStyles.overviewMenuButtons.container(isEditMode),
  button: invoiceAgentsStyles.overviewMenuButtons.button(isEditMode),
});

export const getBlueTileStyles = (receivedData) => ({
  container: invoiceAgentsStyles.blueTile.container(receivedData),
  image: invoiceAgentsStyles.blueTile.image,
});

// Responsive styles for different screen sizes
export const getResponsiveStyles = (isMobile, isTablet) => {
  if (isMobile) {
    return {
      mainContent: {
        flexDirection: "column",
        gap: "16px",
        marginTop: "16px",
      },
      leftContent: {
        width: "100%",
        maxWidth: "100%",
        marginTop: "8px",
      },
      rightContent: {
        width: "100%",
        maxWidth: "100%",
        marginLeft: "0px",
        marginTop: "0px",
        paddingLeft: "0px",
        marginRight: "16px",
      },
      menuButtonSection: {
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "12px",
      },
      overviewMenuButtons: {
        container: (isEditMode) => ({
          width: isEditMode ? "200px" : "100%",
          height: "22px",
          flex: "0 0 auto",
        }),
        button: (isEditMode) => ({
          fontSize: "0.9rem",
          height: "45px",
          width: isEditMode ? "100%" : "100%",
        }),
      },
      blueTile: {
        container: (receivedData) => ({
          width: "100%",
          maxWidth: "100%",
          marginTop: "0px",
          height: "auto",
          minHeight: "140px",
          backgroundColor: receivedData?.name === "Policy" && "#2AD899",
        }),
        image: {
          width: "60px",
        },
      },
      headerTitle: {
        fontSize: "1.25rem",
      },
      bottomContent: {
        width: "100%",
        maxWidth: "100%",
        marginTop: "12px",
        paddingTop: "12px",
        overflowX: "hidden",
        marginRight: 0,
        paddingRight: 0,
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    };
  } else if (isTablet) {
    return {
      mainContent: {
        flexDirection: "row",
        gap: "20px",
        marginTop: "0px",
      },
      leftContent: {
        width: "400px",
        maxWidth: "400px",
        marginTop: "16px",
      },
      rightContent: {
        width: "100%",
        maxWidth: "100%",
        marginLeft: "50px",
        marginTop: "-70px",
        paddingLeft: "12px",
        marginRight: "20px",
      },
      menuButtonSection: {
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
      },
      overviewMenuButtons: {
        container: (isEditMode) => ({
          width: isEditMode ? "180px" : "400px",
          height: "22px",
          flex: "0 0 auto",
        }),
        button: (isEditMode) => ({
          fontSize: "0.95rem",
          height: "48px",
          width: isEditMode ? "100%" : "90%",
        }),
      },
      blueTile: {
        container: (receivedData) => ({
          width: "100%",
          maxWidth: "400px",
          marginTop: "0px",
          height: "auto",
          minHeight: "150px",
          backgroundColor: receivedData?.name === "Policy" && "#2AD899",
        }),
        image: {
          width: "70px",
        },
      },
      headerTitle: {
        fontSize: "1.4rem",
      },
      bottomContent: {
        width: "100%",
        maxWidth: "100%",
        marginTop: "14px",
        paddingTop: "14px",
        overflowX: "hidden",
        marginRight: 0,
        paddingRight: 0,
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    };
  }
  
  return {};
};
